import { useReducer } from 'react';
import { v4 as uuidv4 } from 'uuid';

import type { ISearchPageFilter } from 'api/types/searchPageApiTypes';
import {
  ResponseMakeModelFilterValue,
  ResponseMakeModelType,
  ResponseModelFilter,
} from 'types';

import { findFilterByName } from 'components/SearchPage/mappers/filters';

enum ACTION_TYPE {
  ADD_MAKE_MODEL = 'ADD_MAKE_MODEL',
  REMOVE_MAKE_MODEL = 'REMOVE_MAKE_MODEL',
  UPDATE_MAKE = 'UPDATE_MAKE',
  ADD_MODEL = 'ADD_MODEL',
  REMOVE_MODEL = 'REMOVE_MODEL',
  RESET_MODEL = 'RESET_MODEL',
  RESET = 'RESET',
  KEYWORD_SEARCH = 'KEYWORD_SEARCH',
  POPULATE_MAKE_MODEL_FILTER_WITH_OPTIONS = 'POPULATE_MAKE_MODEL_FILTER_WITH_OPTIONS',
}

type AddMakeModelType = {
  type: ACTION_TYPE.ADD_MAKE_MODEL;
  payload: null;
};

type UpdateMakeType = {
  type: ACTION_TYPE.UPDATE_MAKE;
  payload: {
    id: string;
    value: string;
  };
};

type UpdateModelType = {
  type: ACTION_TYPE.ADD_MODEL;
  payload: {
    id: string;
    value: string;
  };
};

type RemoveModelType = {
  type: ACTION_TYPE.REMOVE_MODEL;
  payload: {
    id: string;
    value: string;
  };
};

type ResetModel = {
  type: ACTION_TYPE.RESET_MODEL;
  payload: {
    id: string;
  };
};

type RemoveMakeModel = {
  type: ACTION_TYPE.REMOVE_MAKE_MODEL;
  payload: {
    id: string;
  };
};

type ResetType = {
  type: ACTION_TYPE.RESET;
  payload: null;
};

type KeywordSearchType = {
  type: ACTION_TYPE.KEYWORD_SEARCH;
  payload: {
    make: string;
    model?: string[];
  };
};

type MakeModelDataType = {
  type: ACTION_TYPE.POPULATE_MAKE_MODEL_FILTER_WITH_OPTIONS;
  payload: {
    filterOptions: ResponseMakeModelFilterValue;
    selectedMakeModelValues?: ResponseModelFilter;
  };
};

type ActionMakeModelType =
  | AddMakeModelType
  | UpdateMakeType
  | UpdateModelType
  | RemoveModelType
  | RemoveMakeModel
  | ResetModel
  | ResetType
  | KeywordSearchType
  | MakeModelDataType;

export type MakeModelItem = {
  id: string;
  makeValue: string;
  modelValue: string[];
  isEditable: boolean;
};

export type MakeModelType = MakeModelItem[];

export type StateMakeModelType = {
  makeModel: MakeModelType;
  makeModelData: {
    make: ResponseMakeModelFilterValue;
    model: ResponseModelFilter;
  };
};

export const defaultState: StateMakeModelType = {
  makeModel: [],
  makeModelData: {
    make: {
      all: [],
      popular: [],
      valueType: 'FilterMakeModelValue',
    },
    model: [],
  },
};

const getMakeModelById = (makeModel: MakeModelType, id: string) => {
  const selectedMakeModel = makeModel.find(
    (makeModelItem) => makeModelItem.id === id,
  );
  const newMakeModel = makeModel.filter(
    (makeModelItem) => makeModelItem.id !== id,
  );
  return {
    selectedMakeModel,
    newMakeModel,
  };
};

const useMakeModelReducer = (
  state: StateMakeModelType,
  action: ActionMakeModelType,
) => {
  switch (action.type) {
    case ACTION_TYPE.ADD_MAKE_MODEL: {
      return {
        ...state,
        makeModel: state.makeModel
          .map((item) => ({
            ...item,
            isEditable: false,
          }))
          .concat([
            {
              ...getNewMakeMode(),
              isEditable: true,
            },
          ]),
      };
    }
    case ACTION_TYPE.UPDATE_MAKE: {
      const { id, value } = action.payload as {
        id: string;
        value: string;
      };
      const { selectedMakeModel, newMakeModel } = getMakeModelById(
        state.makeModel,
        id,
      );
      return selectedMakeModel
        ? {
            ...state,
            makeModel: [
              ...newMakeModel,
              {
                ...selectedMakeModel,
                makeValue: value,
                modelValue: [],
              },
            ],
          }
        : state;
    }
    case ACTION_TYPE.ADD_MODEL: {
      const { id, value } = action.payload as {
        id: string;
        value: string;
      };
      const { selectedMakeModel, newMakeModel } = getMakeModelById(
        state.makeModel,
        id,
      );

      return selectedMakeModel
        ? {
            ...state,
            makeModel: [
              ...newMakeModel,
              {
                ...selectedMakeModel,
                modelValue:
                  value === ''
                    ? []
                    : selectedMakeModel.modelValue.concat([value]),
              },
            ],
          }
        : state;
    }
    case ACTION_TYPE.KEYWORD_SEARCH: {
      const { make, model } = action.payload as {
        make: string;
        model?: string[];
      };
      return {
        ...state,
        makeModel: [
          {
            id: uuidv4(),
            makeValue: make,
            modelValue: model ?? [],
            isEditable: true,
          },
        ],
      };
    }
    case ACTION_TYPE.REMOVE_MODEL: {
      const { id, value } = action.payload as {
        id: string;
        value: string;
      };
      const { selectedMakeModel } = getMakeModelById(state.makeModel, id);
      const removeUpdateMakeModel = state.makeModel.filter(
        (item) => item.id !== id,
      );
      return removeUpdateMakeModel && selectedMakeModel
        ? {
            ...state,
            makeModel: [
              {
                ...selectedMakeModel,
                modelValue: selectedMakeModel.modelValue.filter(
                  (modelValueItem) => modelValueItem !== value,
                ),
              },
              ...removeUpdateMakeModel,
            ],
          }
        : state;
    }
    case ACTION_TYPE.RESET_MODEL: {
      const { id } = action.payload as {
        id: string;
      };
      const { selectedMakeModel } = getMakeModelById(state.makeModel, id);
      const removeUpdateMakeModel = state.makeModel.filter(
        (item) => item.id !== id,
      );
      return removeUpdateMakeModel && selectedMakeModel
        ? {
            ...state,
            makeModel: [
              {
                ...selectedMakeModel,
                modelValue: [],
              },
              ...removeUpdateMakeModel,
            ],
          }
        : state;
    }
    case ACTION_TYPE.REMOVE_MAKE_MODEL: {
      const { id } = action.payload as {
        id: string;
      };
      return {
        ...state,
        makeModel: state.makeModel.filter(
          (makeModelItem) => makeModelItem.id !== id,
        ),
      };
    }
    case ACTION_TYPE.POPULATE_MAKE_MODEL_FILTER_WITH_OPTIONS: {
      const { filterOptions, selectedMakeModelValues } = action.payload;

      const makeModelData = {
        make: filterOptions,
        model: selectedMakeModelValues ?? [],
      };

      return {
        ...state,
        makeModelData,
      };
    }
    case ACTION_TYPE.RESET: {
      return {
        ...state,
        makeModel: [getNewMakeMode()] as MakeModelType,
      };
    }
    default:
      return state;
  }
};

const mapMakeModel = (makeModelParams: ResponseMakeModelType) => {
  return makeModelParams
    ? makeModelParams.map((makeModelItem) => ({
        ...makeModelItem,
      }))
    : [];
};

const getNewMakeMode = () => ({
  id: uuidv4(),
  makeValue: '',
  modelValue: [],
  isEditable: true,
});

const groupByMake = (arrayObjects: any[], key: string) => {
  return arrayObjects.reduce((result, currentObject) => {
    const val = currentObject[key];
    result[val] = result[val] || [];
    currentObject.model && result[val].push(currentObject.model);
    return result;
  }, {});
};

export const groupModelsByMake = (makeModelParams: ResponseMakeModelType) =>
  groupByMake(mapMakeModel(makeModelParams), 'make');

export const useFiltersMakeModel = (
  filters: ISearchPageFilter[],
  makeModelFilterValues: ResponseModelFilter,
  makeModelParams: ResponseMakeModelType,
) => {
  const modelsByMake = groupModelsByMake(makeModelParams);

  const makeModelInitialState = Object.keys(modelsByMake)
    .map((item) => {
      return {
        id: uuidv4(),
        makeValue: item,
        modelValue: modelsByMake[item] ? [...modelsByMake[item]] : [],
        isEditable: false,
      };
    })
    .concat(getNewMakeMode()) || [getNewMakeMode()];

  const makeModelDataFilters = findFilterByName(filters, 'makeModel')?.values;

  const makeModelDataInitialState = {
    make: makeModelDataFilters?.length
      ? (makeModelDataFilters[0] as unknown as ResponseMakeModelFilterValue)
      : ([] as unknown as ResponseMakeModelFilterValue),
    model: makeModelFilterValues,
  };

  const numberNotEditable =
    makeModelInitialState.filter((item) => item.isEditable === false).length ===
    makeModelInitialState.length;

  const initialState: StateMakeModelType = {
    makeModel: makeModelInitialState,
    makeModelData: makeModelDataInitialState,
  };

  const [state, dispatch] = useReducer(useMakeModelReducer, initialState);

  const isAddAnotherEnabled = !!state.makeModel.find(
    (makeModelGroup) =>
      (makeModelGroup.isEditable || numberNotEditable) &&
      makeModelGroup.makeValue.length > 0 &&
      makeModelGroup.makeValue !== 'All Makes' &&
      makeModelGroup.makeValue !== '',
  );

  const addNewMakeModel = () => {
    dispatch({
      type: ACTION_TYPE.ADD_MAKE_MODEL,
      payload: null,
    });
  };

  const handleOnUpdateMake = (id: string, value: string) => {
    dispatch({
      type: ACTION_TYPE.UPDATE_MAKE,
      payload: {
        id,
        value,
      },
    });
  };

  const handleOnAddModel = (id: string, value: string) => {
    dispatch({
      type: ACTION_TYPE.ADD_MODEL,
      payload: {
        id,
        value,
      },
    });
  };

  const addMakeModelWithKeywordSearch = (make: string, model?: string[]) => {
    dispatch({
      type: ACTION_TYPE.KEYWORD_SEARCH,
      payload: {
        make,
        model,
      },
    });
  };

  const handleOnRemoveModel = (id: string, value: string) => {
    dispatch({
      type: ACTION_TYPE.REMOVE_MODEL,
      payload: {
        id,
        value,
      },
    });
  };

  const handleOnRemoveMakeModel = (id: string) => {
    dispatch({
      type: ACTION_TYPE.REMOVE_MAKE_MODEL,
      payload: {
        id,
      },
    });
  };

  const handleResetModel = (id: string) => {
    dispatch({
      type: ACTION_TYPE.RESET_MODEL,
      payload: {
        id,
      },
    });
  };

  const resetMakeModel = () => {
    dispatch({
      type: ACTION_TYPE.RESET,
      payload: null,
    });
  };

  const populateMakeModelFilterWithOptions = (
    filterOptions: ResponseMakeModelFilterValue,
    selectedMakeModelValues?: ResponseModelFilter,
  ) => {
    dispatch({
      type: ACTION_TYPE.POPULATE_MAKE_MODEL_FILTER_WITH_OPTIONS,
      payload: { filterOptions, selectedMakeModelValues },
    });
  };

  const getMakeValues = () => {
    return state.makeModel.reduce<string[]>((group, item) => {
      !!item.makeValue && group.push(item.makeValue);
      return group;
    }, []);
  };

  const getModelValues = () => {
    return state.makeModel.reduce<string[]>((group, item) => {
      if (!!item.modelValue.length) {
        return [...group, ...item.modelValue];
      }
      return group;
    }, []);
  };

  return {
    state: {
      makeModel: state.makeModel,
      makeModelData: state.makeModelData,
      isAddAnotherEnabled: isAddAnotherEnabled,
    },
    dispatch: {
      addNewMakeModel,
      handleOnUpdateMake,
      handleOnAddModel,
      handleOnRemoveModel,
      handleOnRemoveMakeModel,
      handleResetModel,
      resetMakeModel,
      getMakeValues,
      getModelValues,
      addMakeModelWithKeywordSearch,
      populateMakeModelFilterWithOptions,
    },
  };
};

export interface IFilterMakeModelState {
  makeModel: MakeModelType;
  makeModelData: {
    make: ResponseMakeModelFilterValue;
    model: ResponseModelFilter;
  };
}

export interface IFilterMakeModelActions {
  addNewMakeModel: () => void;
  handleOnUpdateMake: (id: string, value: string) => void;
  handleOnAddModel: (id: string, value: string) => void;
  handleOnRemoveModel: (id: string, value: string) => void;
  handleOnRemoveMakeModel: (id: string) => void;
  handleResetModel: (id: string) => void;
  resetMakeModel: () => void;
  getMakeValues: () => string[];
  getModelValues: () => string[];
  addMakeModelWithKeywordSearch: (make: string, model?: string[]) => void;
  populateMakeModelFilterWithOptions: (
    filters: ResponseMakeModelFilterValue,
    selectedMakeModelValues: ResponseModelFilter,
  ) => void;
}
